import { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useStable } from "../context/StableContext";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { HorsePicker } from "../components/HorsePicker";
import { PersonPicker } from "../components/PersonPicker";
import { useIsMobile } from "../hooks/useIsMobile";
import { ChevronLeft, ChevronRight, Plus, Check, X, Pencil, Trash2, Repeat, SlidersHorizontal, Trash, Calendar as CalendarIcon } from "lucide-react";
import { AttendanceSegments, ActivityStatusSegments } from "../components/AttendanceSegments";
import { AttendanceDialog } from "./Calendar";
import { ActivityTimeDialog } from "../components/ActivityTimeDialog";
import { defaultDurationMin, addMinutesHM } from "../lib/activityDefaults";
import { toast } from "sonner";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const RESP = [{ v: "box", l: "Gestione box" }, { v: "preparazione", l: "Preparazione / gestione cavallo" }, { v: "attivita", l: "Attività con il cavallo" }];
const RESP_LABEL = Object.fromEntries(RESP.map((r) => [r.v, r.l]));
const RESP_ORDER = { box: 0, preparazione: 1, attivita: 2 };
// Local-calendar-safe ISO (yyyy-mm-dd). NEVER use toISOString() here: it converts to UTC
// and, in positive-offset timezones (e.g. Italy UTC+1/+2), rolls the date back one day,
// which caused prev/next to jump ±2 days / fail to advance.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDay = (s) => { const d = new Date(s + "T00:00:00"); return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); };

// Calendar lessons belong operationally to the "attivita" area (read-only, Calendar source of truth).
const areaKey = (it) => (it.type === "calendar" ? "attivita" : it.responsibility);
// Identity for the "person" perspective: prefer the Equiro user_id (robust), else the free-text name.
const personKey = (it) => {
  if (it.type === "calendar") return it.instructor_name ? `n:${it.instructor_name}` : "cal";
  if (it.assignee_user_id) return `u:${it.assignee_user_id}`;
  if (it.assignee_name) return `n:${it.assignee_name}`;
  return "none";
};
const personLabel = (it) => {
  if (it.type === "calendar") return it.instructor_name || "Calendario";
  return it.assignee_name || "Non assegnata";
};

// Subtle, DETERMINISTIC person recognition (Piano operational activities only — never Calendar).
// The colour identifies WHO, it is not a workload judgement. Tenue palette, accessible contrast.
const PERSON_PALETTE = [
  { border: "#6E9E8B", bubbleBg: "#E4EFEA", bubbleText: "#2E5B45" },
  { border: "#C89B6B", bubbleBg: "#F4E9D8", bubbleText: "#8C532B" },
  { border: "#8AA0C0", bubbleBg: "#E6ECF5", bubbleText: "#3A4D6B" },
  { border: "#B58AB0", bubbleBg: "#F1E6EF", bubbleText: "#6B3A63" },
  { border: "#A0A86E", bubbleBg: "#EEEFDD", bubbleText: "#5B5E2E" },
  { border: "#C98A8A", bubbleBg: "#F5E6E6", bubbleText: "#6B3A3A" },
  { border: "#7FB0B5", bubbleBg: "#E1EFF0", bubbleText: "#2E5B5E" },
  { border: "#B69BC9", bubbleBg: "#EDE6F3", bubbleText: "#553A6B" },
];
function personAccent(key) {
  const s = String(key || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PERSON_PALETTE[h % PERSON_PALETTE.length];
}
function PersonBubble({ name, accent, testId }) {
  const initials = (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
  return (
    <span data-testid={testId} title={name}
      className="shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold"
      style={{ backgroundColor: accent.bubbleBg, color: accent.bubbleText }}>{initials}</span>
  );
}

// Aggregate Calendar binomi by REAL lesson event id (not by time/title) — display-only, Calendar
// stays source of truth. Each row remains an individual binomio with its own Presenze.
function lessonBlocksOf(items) {
  const m = new Map();
  for (const it of items) {
    if (it.type !== "calendar") continue;
    if (!m.has(it.lesson_id)) m.set(it.lesson_id, { lesson_id: it.lesson_id, occ_key: it.occ_key, date: it.date, time: it.time, end_time: it.end_time, series_id: it.series_id, instructor_id: it.instructor_id, title: it.title, instructor_name: it.instructor_name, rows: [] });
    m.get(it.lesson_id).rows.push(it);
  }
  return [...m.values()].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
}

// Per area "Altre attività": group by activity type, chrono then horse tie-break.
function groupActsByType(acts) {
  const m = new Map();
  for (const it of acts) { const k = it.activity_type_label || it.activity_type_id || "—"; if (!m.has(k)) m.set(k, []); m.get(k).push(it); }
  return [...m.entries()].map(([label, rows]) => ({ label, rows: rows.slice().sort((a, b) => (a.time || "").localeCompare(b.time || "") || (a.horse_name || "").localeCompare(b.horse_name || "")) }))
    .sort((a, b) => ((a.rows[0] || {}).time || "~").localeCompare((b.rows[0] || {}).time || "~") || a.label.localeCompare(b.label));
}

export default function ActivityPlan() {
  const { current, perms } = useStable();
  const { user } = useAuth();
  const { setPageTitle } = useOutletContext() || {};
  const canManage = perms?.canManage;
  const isMobile = useIsMobile();
  const [date, setDate] = useState(iso(new Date()));
  const [attOcc, setAttOcc] = useState(null);
  const [view, setView] = useState("horse");
  const [plan, setPlan] = useState({ items: [], counts: { todo: 0, done: 0 } });
  const [horses, setHorses] = useState([]);
  const [types, setTypes] = useState([]);
  const [people, setPeople] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingEdit, setPendingEdit] = useState(null);
  useEffect(() => {
    const eid = searchParams.get("edit");
    if (!eid) return;
    const ed = searchParams.get("editdate");
    if (ed) setDate(ed);
    setPendingEdit(eid);
    const sp = new URLSearchParams(searchParams); sp.delete("edit"); sp.delete("editdate"); setSearchParams(sp, { replace: true });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pendingEdit) return;
    const found = plan.items.find((i) => i.id === pendingEdit && i.type === "activity");
    if (found) { setEditItem(found); setPendingEdit(null); }
  }, [plan.items, pendingEdit]);
  const [confirm, setConfirm] = useState(null);
  const [timeDlg, setTimeDlg] = useState(null);
  const [routinesOpen, setRoutinesOpen] = useState(false);
  const [routines, setRoutines] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [noAct, setNoAct] = useState(null);
  const [noActShow, setNoActShow] = useState(false);
  // Filters (combinable, default "" = Tutti/Tutte). Grouping (view) != filtering.
  const [fHorse, setFHorse] = useState("");
  const [fPersona, setFPersona] = useState("");
  const [fArea, setFArea] = useState("");

  useEffect(() => { if (setPageTitle) setPageTitle("Attività cavalli"); }, [setPageTitle]);

  const load = useCallback(async () => {
    if (!current) return;
    try {
      const r = await api.get(`/stables/${current.id}/activity-plan?date=${date}&now=${localIso()}`);
      setPlan(r.data);
      api.get(`/stables/${current.id}/activity-plan/no-activity?date=${date}`).then((na) => setNoAct(na.data)).catch(() => setNoAct(null));
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  }, [current, date]);
  useEffect(() => { load(); }, [load]);

  const loadPeople = useCallback(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/people`).then((r) => setPeople(r.data || [])).catch(() => setPeople([]));
  }, [current]);
  useEffect(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/horses`).then((r) => setHorses((r.data || []).filter((h) => !h.archived))).catch(() => {});
    api.get(`/stables/${current.id}/activity-types`).then((r) => setTypes(r.data || [])).catch(() => {});
    loadPeople();
  }, [current, loadPeople]);

  const shift = (n) => { const d = new Date(date + "T00:00:00"); d.setDate(d.getDate() + n); setDate(iso(d)); };

  const setStatus = async (it, status, skip_reason) => {
    const actual_end_at = status === "done" ? localIso() : undefined;
    try { const r = await api.patch(`/stables/${current.id}/activities/${it.id}/status`, { status, skip_reason, actual_end_at }); load(); return r; }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const openStart = (it) => setTimeDlg({ mode: "start", item: it });
  const openFinish = (it) => setTimeDlg({ mode: "finish", item: it, minTime: it.actual_start_at });
  const doStart = async (it, iso) => {
    try { await api.post(`/stables/${current.id}/activities/${it.id}/start`, { actual_start_at: iso }); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const doFinish = async (it, iso) => {
    try { await api.post(`/stables/${current.id}/activities/${it.id}/finish`, { actual_end_at: iso }); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const doConfirm = async (it) => {
    try { await api.post(`/stables/${current.id}/activities/${it.id}/confirm`); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const canOps = !perms?.assist;
  // Open the EXISTING Presenze component (Calendar's AttendanceDialog) INLINE over Attività — NO
  // navigation. Build the occurrence object from the item's real fields (occ_key = real occ id).
  // On close we simply reload the plan; date/view/filters live in state and are untouched.
  const openLesson = (o) => {
    if (!o || !o.occ_key) return;
    setAttOcc({
      id: o.occ_key, date: o.date || date, start_time: o.start_time || o.time || null,
      end_time: o.end_time || null, series_id: o.series_id || null,
      instructor_id: o.instructor_id ?? null, instructor_name: o.instructor_name ?? null,
    });
  };
  // Inline Presenze on Calendar rows — writes to the EXISTING attendance domain/API (no parallel state).
  const setPresence = async (item, rider, status) => {
    try {
      const { data } = await api.get(`/stables/${current.id}/calendar/attendance?occ_key=${encodeURIComponent(item.occ_key)}`);
      const occ = data.occurrence || {};
      let entries = ((data.attendance && data.attendance.entries) || (data.template && data.template.entries) || []).map((e) => ({ ...e }));
      const norm = (s) => (s || "").trim().toLowerCase();
      let found = entries.find((e) => norm(e.name) === norm(rider.name));
      if (!found) { found = { name: rider.name, status: "unrecorded", planned_horse_id: item.horse_id }; entries.push(found); }
      found.status = status;
      await api.put(`/stables/${current.id}/calendar/attendance`, {
        occ_key: item.occ_key, series_id: (data.attendance && data.attendance.series_id) ?? occ.series_id ?? item.series_id ?? null,
        date: item.date, start_time: item.time, end_time: item.end_time,
        instructor_id: item.instructor_id ?? null, instructor_name: item.instructor_name ?? null, entries,
      });
      toast.success("Presenza aggiornata"); load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    const it = confirm; setConfirm(null);
    try {
      if (it.kind === "routine") { await api.delete(`/stables/${current.id}/activity-routines/${it.id}`); loadRoutines(); }
      else { await api.delete(`/stables/${current.id}/activities/${it.id}`); }
      toast.success("Eliminato"); load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const loadRoutines = useCallback(async () => {
    try { const r = await api.get(`/stables/${current.id}/activity-routines`); setRoutines(r.data || []); } catch { /* */ }
  }, [current]);
  const terminate = async (r) => {
    try { await api.patch(`/stables/${current.id}/activity-routines/${r.id}`, { active: false, end_date: date }); toast.success("Routine terminata"); loadRoutines(); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  // Person picker options built from the day's plan (robust user_id keys + name fallback).
  const personaOptions = useMemo(() => {
    const map = new Map();
    for (const it of plan.items) { const k = personKey(it); if (k !== "none") map.set(k, personLabel(it)); }
    const arr = [...map.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    return arr;
  }, [plan.items]);
  const myKey = user?.id ? `u:${user.id}` : null;
  const hasMe = myKey && plan.items.some((it) => personKey(it) === myKey);
  const me = useMemo(() => {
    const p = people.find((x) => x.user_id && x.user_id === user?.id);
    return p ? { personId: p.id, name: p.name } : null;
  }, [people, user]);

  const filtered = useMemo(() => {
    let items = plan.items;
    if (fHorse) items = items.filter((i) => i.horse_id === fHorse);
    if (fArea) items = items.filter((i) => areaKey(i) === fArea);
    if (fPersona) items = items.filter((i) => personKey(i) === fPersona);
    return items;
  }, [plan.items, fHorse, fArea, fPersona]);

  const groups = useMemo(() => {
    const map = new Map();
    const meta = new Map(); // key -> {label, sort}
    for (const it of filtered) {
      let k, label, sort;
      if (view === "horse") { k = it.horse_id; label = it.horse_name; sort = (it.horse_name || "").toLowerCase(); }
      else if (view === "person") { k = personKey(it); label = personLabel(it); sort = (label || "").toLowerCase(); }
      else { k = areaKey(it); label = RESP_LABEL[k] || k; sort = RESP_ORDER[k] ?? 9; }
      if (!map.has(k)) { map.set(k, []); meta.set(k, { label, sort }); }
      map.get(k).push(it);
    }
    return [...map.entries()].map(([k, rows]) => ({ k, rows, ...meta.get(k) }))
      .sort((a, b) => (typeof a.sort === "number" ? a.sort - b.sort : String(a.sort).localeCompare(String(b.sort))));
  }, [filtered, view]);

  const filtersActive = fHorse || fPersona || fArea;
  const resetFilters = () => { setFHorse(""); setFPersona(""); setFArea(""); };
  const rowEl = (it) => (
    <PlanRow key={it.id} it={it} view={view} canManage={canManage} canOps={canOps} onPresence={setPresence} onOpenLesson={openLesson}
      onStatus={(s) => setStatus(it, s)} onStart={() => openStart(it)} onFinish={() => openFinish(it)} onConfirm={() => doConfirm(it)} onEdit={() => setEditItem(it)}
      onDelete={() => setConfirm({ ...it, kind: "activity" })} />
  );
  const calItems = useMemo(() => filtered.filter((i) => i.type === "calendar"), [filtered]);
  const actItems = useMemo(() => filtered.filter((i) => i.type === "activity"), [filtered]);
  const lessons = useMemo(() => lessonBlocksOf(calItems), [calItems]);
  const personGroups = useMemo(() => {
    const m = new Map(); const meta = new Map();
    for (const it of actItems) { const k = personKey(it); if (!m.has(k)) { m.set(k, []); meta.set(k, personLabel(it)); } m.get(k).push(it); }
    return [...m.entries()].map(([k, rows]) => ({ k, none: k === "none", label: k === "none" ? "Non assegnate" : meta.get(k), rows: rows.sort((a, b) => (a.time || "").localeCompare(b.time || "")) }))
      .sort((a, b) => (a.none ? 1 : b.none ? -1 : String(a.label).localeCompare(String(b.label))));
  }, [actItems]);

  const filterControls = (
    <>
      <div className="min-w-[180px]">
        <Label className="text-[11px] text-[#5F6B64]">Cavallo</Label>
        <div className="mt-1">
          <HorsePicker testId="filter-horse" horses={horses} value={fHorse}
            onChange={(id) => setFHorse(id || "")} placeholder="Tutti i cavalli" />
        </div>
      </div>
      <div className="min-w-[160px]">
        <Label className="text-[11px] text-[#5F6B64]">Persona</Label>
        <Select value={fPersona || "all"} onValueChange={(v) => setFPersona(v === "all" ? "" : v)}>
          <SelectTrigger className="mt-1 h-9" data-testid="filter-persona"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            {hasMe && <SelectItem value={myKey}>Io</SelectItem>}
            {personaOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-[150px]">
        <Label className="text-[11px] text-[#5F6B64]">Area</Label>
        <Select value={fArea || "all"} onValueChange={(v) => setFArea(v === "all" ? "" : v)}>
          <SelectTrigger className="mt-1 h-9" data-testid="filter-area"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            {RESP.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </>
  );
  // Desktop: one coherent toolbar — inline compact pickers, all h-9, aligned with the view tabs.
  const filterControlsInline = (
    <>
      <div className="w-[160px]">
        <HorsePicker testId="filter-horse" horses={horses} value={fHorse}
          onChange={(id) => setFHorse(id || "")} placeholder="Cavallo: tutti" />
      </div>
      <Select value={fPersona || "all"} onValueChange={(v) => setFPersona(v === "all" ? "" : v)}>
        <SelectTrigger className="h-9 w-[150px]" data-testid="filter-persona"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Persona: tutte</SelectItem>
          {hasMe && <SelectItem value={myKey}>Io</SelectItem>}
          {personaOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={fArea || "all"} onValueChange={(v) => setFArea(v === "all" ? "" : v)}>
        <SelectTrigger className="h-9 w-[140px]" data-testid="filter-area"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Area: tutte</SelectItem>
          {RESP.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </>
  );

  return (
    <div className="space-y-5 eq-fade-up" data-testid="activity-plan-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Button variant="outline" size="icon" data-testid="plan-prev" onClick={() => shift(-1)} className="h-9 w-9"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-[140px] sm:min-w-[190px] text-center">
            <p className="font-display font-bold text-[#1B3B2B] capitalize" data-testid="plan-date-label">{fmtDay(date)}</p>
            <button data-testid="plan-today" onClick={() => setDate(iso(new Date()))} className="text-xs text-[#8C532B] hover:underline">Oggi</button>
          </div>
          <Button variant="outline" size="icon" data-testid="plan-next" onClick={() => shift(1)} className="h-9 w-9"><ChevronRight className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} data-testid="plan-date-picker" className="h-9 w-full sm:w-[150px]" aria-label="Vai a una data" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#5F6B64]" data-testid="plan-counts">{plan.counts?.todo ?? 0} da fare · {plan.counts?.done ?? 0} completate</span>
          {canManage && <Button variant="outline" size="sm" data-testid="open-routines" onClick={() => { setRoutinesOpen(true); loadRoutines(); }}><Repeat className="h-4 w-4 mr-1" />Routine</Button>}
          <Button size="sm" data-testid="new-activity-btn" onClick={() => setCreateOpen(true)} className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Plus className="h-4 w-4 mr-1" />Nuova attività</Button>
        </div>
      </div>

      {/* Views (grouping) + combinable filters — one coherent toolbar on desktop */}
      {noAct?.enabled && noAct.count > 0 && (
        <div data-testid="no-activity-alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-center gap-2 flex-wrap">
          <span>{noAct.count} cavall{noAct.count === 1 ? "o" : "i"} senza attività prevista oggi</span>
          <button data-testid="no-activity-show" onClick={() => setNoActShow((s) => !s)} className="text-[#8C532B] underline text-xs">{noActShow ? "Nascondi" : "Mostra"}</button>
          {noActShow && <span className="w-full text-xs text-amber-800">{noAct.horses.map((h) => h.display_name).join(" · ")}</span>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 bg-[#F0EAE1] rounded-lg p-1 h-9 items-center w-fit">
          {[["horse", "Per cavallo"], ["person", "Per persona"], ["area", "Per area"]].map(([v, l]) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`px-3 h-7 rounded-md text-sm font-medium transition-colors ${view === v ? "bg-white text-[#1B3B2B] shadow-sm" : "text-[#5F6B64]"}`}>{l}</button>
          ))}
        </div>
        {isMobile ? (
          <Button variant="outline" size="sm" data-testid="open-filters" onClick={() => setFiltersOpen(true)}
            className={`h-9 ${filtersActive ? "border-[#1B3B2B] text-[#1B3B2B]" : ""}`}>
            <SlidersHorizontal className="h-4 w-4 mr-1" />Filtri{filtersActive ? " •" : ""}
          </Button>
        ) : (
          <>
            <span className="hidden lg:block w-px h-6 bg-[#E5DEC9] mx-1" />
            {filterControlsInline}
            {filtersActive ? (
              <Button variant="ghost" size="sm" data-testid="reset-filters" onClick={resetFilters} className="text-[#8C532B] h-9">
                <Trash className="h-3.5 w-3.5 mr-1" />Azzera filtri
              </Button>
            ) : null}
          </>
        )}
      </div>

      {groups.length === 0 && <p className="text-sm text-[#9AA39C] py-8 text-center" data-testid="plan-empty">Nessuna attività per questo giorno{filtersActive ? " con i filtri attivi" : ""}.</p>}
      <div className="space-y-5">
        {view === "horse" && (
          <div className="space-y-4" data-testid="horse-list">
            {groups.map((g) => (
              <SectionCard key={g.k} testId={`plan-group-${g.k}`} label={g.label} level="horse">
                {g.rows.map((it) => rowEl(it))}
              </SectionCard>
            ))}
          </div>
        )}

        {view === "area" && RESP.map((r) => {
          const acts = actItems.filter((i) => i.responsibility === r.v).sort((a, b) => (a.time || "").localeCompare(b.time || ""));
          if (r.v !== "attivita") {
            if (!acts.length) return null;
            return <SectionCard key={r.v} testId={`plan-group-${r.v}`} label={r.l}>{acts.map((it) => rowEl(it))}</SectionCard>;
          }
          if (!acts.length && !lessons.length) return null;
          return (
            <div key="attivita" data-testid="plan-group-attivita">
              <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#8C532B] mb-1.5">{RESP_LABEL.attivita}</p>
              <div className="grid gap-3 lg:grid-cols-2">
                <div className="min-w-0 order-2 lg:order-1">
                  <p className="text-[11px] font-semibold text-[#5F6B64] mb-1">Lezioni</p>
                  {lessons.length ? <div className="space-y-2">{lessons.map((b) => <LessonBlock key={b.lesson_id} block={b} canOps={canOps} setPresence={setPresence} onOpen={openLesson} />)}</div>
                    : <p className="text-xs text-[#9AA39C] py-2">Nessuna lezione.</p>}
                </div>
                <div className="min-w-0 order-1 lg:order-2">
                  <p className="text-[11px] font-semibold text-[#5F6B64] mb-1">Altre attività</p>
                  {acts.length ? <div className="space-y-3">{groupActsByType(acts).map((g) => (
                    <div key={g.label} data-testid={`type-group-${g.label}`}>
                      <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#8C6D4F] mb-1">{g.label}</p>
                      <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">{g.rows.map((it) => rowEl(it))}</div>
                    </div>
                  ))}</div>
                    : <p className="text-xs text-[#9AA39C] py-2">Nessuna attività.</p>}
                </div>
              </div>
            </div>
          );
        })}

        {view === "person" && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start" data-testid="person-columns">
            <div className="space-y-2 order-2 xl:order-1" data-testid="person-col-lessons">
              <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#8C532B] mb-1.5">Lezioni</p>
              {lessons.length ? lessons.map((b) => <LessonBlock key={b.lesson_id} block={b} canOps={canOps} setPresence={setPresence} onOpen={openLesson} />)
                : <p className="text-xs text-[#9AA39C] py-2">Nessuna lezione.</p>}
            </div>
            <div className="space-y-5 order-1 xl:order-2" data-testid="person-col-people">
              <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#8C532B] mb-1.5">Persone</p>
              {personGroups.length === 0 && <p className="text-xs text-[#9AA39C] py-2">Nessuna attività assegnata.</p>}
              {personGroups.map((g) => (
                <SectionCard key={g.k} testId={`plan-group-${g.k}`} label={g.label}
                  accent={!g.none ? personAccent(g.k) : null}>{g.rows.map((it) => rowEl(it))}</SectionCard>
              ))}
            </div>
          </div>
        )}
      </div>

      {createOpen && <CreateActivity current={current} horses={horses} types={types} people={people} me={me} canManage={canManage} canOps={canOps}
        isMobile={isMobile} date={date} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); loadPeople(); }} />}
      {editItem && <EditActivity current={current} horses={horses} types={types} people={people} me={me} canOps={canOps} isMobile={isMobile}
        linkedPreps={plan.items.filter((i) => i.type === "activity" && i.linked_activity_id === editItem.id)}
        it={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(); loadPeople(); }} />}

      {/* Mobile filters sheet */}
      <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
        <DialogContent data-testid="filters-dialog" className="max-w-[400px]">
          <DialogHeader><DialogTitle>Filtri</DialogTitle>
            <DialogDescription>Combina cavallo, persona e area. Il raggruppamento resta la vista scelta.</DialogDescription></DialogHeader>
          <div className="space-y-3">{filterControls}</div>
          <DialogFooter className="gap-2">
            {filtersActive && <Button variant="ghost" data-testid="reset-filters-mobile" onClick={resetFilters} className="text-[#8C532B]"><Trash className="h-3.5 w-3.5 mr-1" />Azzera</Button>}
            <Button className="bg-[#1B3B2B] hover:bg-[#142E1F]" onClick={() => setFiltersOpen(false)}>Applica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Conferma eliminazione</DialogTitle>
            <DialogDescription>
              {confirm?.kind === "routine" ? "La routine verrà eliminata: interrompe le occorrenze future e rimuove quelle non ancora eseguite. Lo storico Fatto/Saltato resta." :
                confirm?.source === "routine" ? "Questa occorrenza di routine non verrà più mostrata (eccezione per questo giorno). La routine futura resta invariata." :
                  "L'attività verrà eliminata definitivamente."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Annulla</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn" onClick={doDelete}>Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={routinesOpen} onOpenChange={setRoutinesOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="routines-sheet">
          <SheetHeader><SheetTitle>Routine attive</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-2">
            {routines.length === 0 && <p className="text-sm text-[#9AA39C]">Nessuna routine.</p>}
            {routines.map((r) => (
              <div key={r.id} data-testid={`routine-${r.id}`} className="rounded-lg border border-[#E5DEC9] p-3">
                <p className="font-medium text-sm text-[#1B3B2B]">{r.horse_name} — {r.activity_type_label}</p>
                <p className="text-xs text-[#5F6B64]">{(r.weekdays || []).map((w) => WD[w]).join(", ")} · {r.start_date}{r.end_date ? `→${r.end_date}` : ""}{r.time ? ` · ${r.time}` : ""}{r.assignee_name ? ` · ${r.assignee_name}` : ""}{r.active ? "" : " · terminata"}</p>
                <div className="mt-2 flex gap-2">
                  {r.active && <Button size="sm" variant="outline" data-testid={`routine-terminate-${r.id}`} onClick={() => terminate(r)}>Termina</Button>}
                  <Button size="sm" variant="outline" className="text-red-600" data-testid={`routine-delete-${r.id}`} onClick={() => setConfirm({ id: r.id, kind: "routine" })}>Elimina</Button>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <AttendanceDialog open={!!attOcc} onClose={() => setAttOcc(null)} stableId={current?.id} occ={attOcc}
        horses={horses} canWrite={!!perms?.canWrite} onChanged={() => load()} />

      {timeDlg && (
        <ActivityTimeDialog open mode={timeDlg.mode} date={timeDlg.item.date} minTime={timeDlg.minTime}
          onClose={() => setTimeDlg(null)}
          onConfirm={(iso) => { const t = timeDlg; setTimeDlg(null); if (t.mode === "start") doStart(t.item, iso); else doFinish(t.item, iso); }} />
      )}
    </div>
  );
}

function personEngText(e) {
  const range = e.time ? (e.end ? `dalle ${e.time} alle ${e.end}` : `alle ${e.time}`) : "";
  if (e.kind === "instructor") return `come istruttore nella lezione «${e.lesson}»${range ? " " + range : ""}`;
  if (e.kind === "rider") return `come allievo nella lezione «${e.lesson}»${range ? " " + range : ""}`;
  return `attività «${e.label}»${e.time ? ` alle ${e.time}` : ""}${e.horse ? ` · ${e.horse}` : ""}`;
}

function SectionCard({ label, testId, children, level, accent }) {
  const header = level === "horse" ? (
    <div className="flex items-center gap-2 mb-1.5">
      <span className="h-2 w-2 rounded-full bg-[#8C532B] shrink-0" />
      <p className="font-display text-base font-bold text-[#1B3B2B] leading-tight truncate" data-testid={testId ? `${testId}-name` : undefined}>{label}</p>
    </div>
  ) : (
    <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#8C532B] mb-1.5 flex items-center gap-1.5">
      {accent && <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: accent.border }} />}{label}
    </p>
  );
  return (
    <div data-testid={testId}>
      {header}
      <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">{children}</div>
    </div>
  );
}

// Aggregated Calendar lesson: one block per real event, binomi listed below with per-binomio Presenze.
function LessonBlock({ block, canOps, setPresence, onOpen }) {
  const open = onOpen ? () => onOpen(block) : undefined;
  return (
    <div data-testid={`lesson-block-${block.lesson_id}`} className={`bg-white rounded-xl border border-[#E5DEC9] overflow-hidden ${onOpen ? "hover:border-[#8C532B] transition-colors" : ""}`}>
      <div data-testid={`open-lesson-${block.lesson_id}`} onClick={open}
        className={`flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 bg-[#FBF9F4] border-b border-[#F0EAE1] ${onOpen ? "cursor-pointer" : ""}`}>
        <span className="text-xs font-mono-label text-[#8C532B] shrink-0">{block.time || "—"}</span>
        <span className="text-sm font-semibold text-[#1B3B2B] truncate min-w-0 flex-1">{block.title}</span>
        <span className="inline-flex items-center gap-1 text-[10px] text-[#9AA39C] shrink-0"><CalendarIcon className="h-3 w-3" />Calendario</span>
        <span className="text-[10px] text-[#5F6B64] shrink-0">{block.rows.length} binom{block.rows.length === 1 ? "io" : "i"}</span>
      </div>
      <div className="divide-y divide-[#F0EAE1]">
        {block.rows.map((it) => {
          const riders = it.riders || [];
          return (
            <div key={it.id} className="px-3 py-2 text-sm" data-testid={`lesson-row-${it.id}`}>
              {riders.length === 0 ? (
                <p onClick={open} className={`text-[#1B3B2B] break-words ${onOpen ? "cursor-pointer" : ""}`}><span className="font-semibold">{it.horse_name}</span> <span className="text-[#9AA39C] italic text-xs">— nessun rider</span></p>
              ) : riders.map((r, i) => (
                <div key={i} className="flex items-start justify-between gap-2" data-testid={`cal-rider-${it.id}-${i}`}>
                  <span onClick={open} className={`min-w-0 flex-1 ${onOpen ? "cursor-pointer" : ""} ${r.relation === "moved_away" ? "opacity-60" : ""}`}>
                    <span className="text-[#1B3B2B]"><span className="font-semibold">{it.horse_name}</span> — {r.name}</span>
                    {r.relation === "actual" && r.planned_horse_name && <span className="block text-[11px] text-[#8C6D4F]" data-testid={`cal-rider-note-${it.id}-${i}`}>Previsto: {r.planned_horse_name}</span>}
                    {r.relation === "moved_away" && <span className="block text-[11px] text-[#8C6D4F]" data-testid={`cal-rider-note-${it.id}-${i}`}>Ha svolto con {r.actual_horse_name || "altro cavallo"}</span>}
                  </span>
                  {setPresence && <span onClick={(e) => e.stopPropagation()}><AttendanceSegments status={r.att_status} disabled={!canOps} onSelect={(s) => setPresence(it, r, s)} testId={`presence-${it.id}-${i}`} /></span>}
                </div>
              ))}
              {block.instructor_name && <p className="text-[11px] text-[#5F6B64] mt-0.5">Istr. {block.instructor_name}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const localIso = () => { const d = new Date(); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`; };
const nowHM = () => { const d = new Date(); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getHours())}:${p(d.getMinutes())}`; };
const hhmm = (iso) => (iso ? String(iso).slice(11, 16) : "");

function PlanRow({ it, view, canManage, canOps, onStatus, onStart, onFinish, onConfirm, onEdit, onDelete, onPresence, onOpenLesson }) {
  const isCal = it.type === "calendar";
  const outside = it.horse_outside;
  if (isCal) {
    const riders = it.riders || [];
    const openIt = onOpenLesson ? () => onOpenLesson(it) : undefined;
    return (
      <div data-testid={`plan-row-${it.id}`} className={`flex items-start gap-3 px-3 py-2.5 ${outside ? "opacity-50" : ""}`}>
        <span className="w-12 text-xs font-mono-label text-[#8C532B] shrink-0 pt-0.5">{it.time || "—"}</span>
        <div className="min-w-0 flex-1">
          <p data-testid={`open-lesson-${it.id}`} onClick={openIt} className={`text-sm text-[#1B3B2B] truncate ${openIt ? "cursor-pointer hover:text-[#8C532B]" : ""}`}>
            {view === "horse" ? <span className="font-semibold">{it.title}</span> : <><span className="font-semibold">{it.horse_name}</span> · {it.title}</>}
            <span data-testid="cal-badge" className="ml-2 inline-flex items-center gap-1 align-middle text-[10px] text-[#9AA39C]"><CalendarIcon className="h-3 w-3" />Calendario</span>
          </p>
          <div className="text-xs text-[#5F6B64] space-y-1 mt-1">
            {riders.length === 0 && <p className="italic text-[#9AA39C]">Nessun rider pianificato</p>}
            {riders.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-2" data-testid={`cal-rider-${it.id}-${i}`}>
                <span onClick={openIt} className={`min-w-0 flex-1 ${openIt ? "cursor-pointer" : ""} ${r.relation === "moved_away" ? "opacity-60" : ""}`}>
                  <span className="font-medium text-[#1B3B2B]">{r.name}</span>
                  {r.relation === "actual" && r.planned_horse_name && <span className="block text-[11px] text-[#8C6D4F]" data-testid={`cal-rider-note-${it.id}-${i}`}>Previsto: {r.planned_horse_name}</span>}
                  {r.relation === "moved_away" && <span className="block text-[11px] text-[#8C6D4F]" data-testid={`cal-rider-note-${it.id}-${i}`}>Ha svolto con {r.actual_horse_name || "altro cavallo"}</span>}
                </span>
                {onPresence && <span onClick={(e) => e.stopPropagation()}><AttendanceSegments status={r.att_status} disabled={!canOps} onSelect={(s) => onPresence(it, r, s)} testId={`presence-${it.id}-${i}`} /></span>}
              </div>
            ))}
            {view !== "person" && it.instructor_name && <p>Istr. {it.instructor_name}</p>}
          </div>
        </div>
      </div>
    );
  }
  const label = it.activity_type_label;
  const person = personLabel(it);
  const pkey = personKey(it);
  const accent = (view !== "person" && pkey !== "none") ? personAccent(pkey) : null;
  const live = it.live;
  const startable = !it.actual_start_at && !it.actual_end_at;
  const startHM = it.actual_start_at ? hhmm(it.actual_start_at) : (it.start_at ? hhmm(it.start_at) : (it.time || ""));
  const expHM = it.expected_end_at ? hhmm(it.expected_end_at) : "";
  const overdue = live && it.expected_end_at && new Date() > new Date(it.expected_end_at);
  let mainNode, subText;
  if (view === "horse") { mainNode = <span className="font-medium text-[#1B3B2B]">{label}</span>; subText = person; }
  else if (view === "person") { mainNode = <span className="font-semibold text-[#1B3B2B]">{it.horse_name}</span>; subText = label; }
  else { mainNode = <><span className="font-semibold text-[#1B3B2B]">{it.horse_name}</span>{person && person !== "Non assegnata" ? <span className="text-[#5F6B64] font-normal"> — {person}</span> : null}</>; subText = label; }
  return (
    <div data-testid={`plan-row-${it.id}`} className={`flex items-center gap-3 px-3 py-2.5 ${outside ? "opacity-50" : ""}`}
      style={accent ? { borderLeft: `3px solid ${accent.border}` } : undefined}>
      <span className="w-12 text-xs font-mono-label text-[#8C532B] shrink-0">{(live ? startHM : it.time) || "—"}</span>
      {accent && <PersonBubble name={person} accent={accent} testId={`person-bubble-${it.id}`} />}
      <div className="min-w-0 flex-1">
        <p className="text-sm truncate">{mainNode}
          {live && <span data-testid={`act-live-badge-${it.id}`} className="ml-1.5 inline-flex align-middle text-[10px] font-semibold text-[#1B5E20] bg-[#E8F5E9] rounded-full px-1.5 py-0.5">In corso</span>}
          {it.pending_confirmation && <span data-testid={`act-toconfirm-badge-${it.id}`} className="ml-1.5 inline-flex align-middle text-[10px] font-semibold text-[#8C532B] bg-[#F7EFE0] rounded-full px-1.5 py-0.5">Da confermare</span>}
          {(it.to_confirm && !it.pending_confirmation) && <span data-testid={`act-toconfirm-badge-${it.id}`} className="ml-1.5 inline-flex align-middle text-[10px] font-semibold text-[#8C532B] bg-[#F7EFE0] rounded-full px-1.5 py-0.5">Da confermare</span>}
          {overdue && <span data-testid={`act-overdue-${it.id}`} className="ml-1 inline-flex align-middle text-[10px] font-semibold text-[#8C532B] bg-[#F7EFE0] rounded-full px-1.5 py-0.5">rientro superato</span>}
          {it.linked_activity_id && <span className="ml-1 text-[10px] text-[#8C6D4F]">· collegata</span>}</p>
        {live
          ? <p className="text-xs text-[#1B5E20] font-medium truncate" data-testid={`act-live-info-${it.id}`}>{subText} · {startHM || "—"}{expHM ? `–${expHM}` : ""}</p>
          : it.to_confirm
            ? <p className="text-xs text-[#8C532B] font-medium truncate" data-testid={`act-toconfirm-info-${it.id}`}>{subText} · Da confermare</p>
            : <p className="text-xs text-[#5F6B64] truncate">{subText}{outside ? " · Cavallo fuori scuderia" : ""}{it.status === "skipped" && it.skip_reason ? ` · ${it.skip_reason}` : ""}</p>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {it.to_confirm
          ? <Button size="sm" variant="outline" disabled={!canOps} data-testid={`act-confirm-${it.id}`} onClick={() => onConfirm()} className="h-7 text-xs border-[#8C532B] text-[#8C532B] hover:bg-[#F7EFE0]">Da confermare</Button>
          : live
            ? <Button size="sm" variant="outline" disabled={!canOps} data-testid={`act-finish-${it.id}`} onClick={() => onFinish()} className="h-7 text-xs border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9]">Termina</Button>
            : startable
              ? <Button size="sm" variant="outline" disabled={!canOps} data-testid={`act-start-${it.id}`} onClick={() => onStart()} className="h-7 text-xs border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9]">Avvia</Button>
              : <ActivityStatusSegments status={it.status} disabled={!canOps} onSelect={onStatus} testId={`act-status-${it.id}`} size="sm" />}
        <button data-testid={`act-edit-${it.id}`} title="Modifica" onClick={onEdit} className="h-7 w-7 rounded-md text-[#8C6D4F] hover:bg-[#F0EAE1] flex items-center justify-center"><Pencil className="h-3.5 w-3.5" /></button>
        {canManage && <button data-testid={`act-delete-${it.id}`} title="Elimina" onClick={onDelete} className="h-7 w-7 rounded-md text-red-600 hover:bg-red-50 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

// "Assegnato a" — a Persona/Collaboratore from the anagrafica (robust) or a free-text name.
function AssigneeBlock({ people, me, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs">Assegnato a</Label>
      <div className="mt-1"><PersonPicker testId={testId} people={people} me={me} value={value} onChange={onChange} /></div>
    </div>
  );
}

// Responsive shell: centered wide Dialog on desktop, right Sheet on mobile. Same inner form.
function FormShell({ isMobile, title, testId, onClose, children }) {
  if (isMobile) {
    return (
      <Sheet open onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid={testId}>
          <SheetHeader><SheetTitle>{title}</SheetTitle></SheetHeader>
          <div className="mt-4">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testId} className="max-w-[820px] max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

// "Assegnato a" — a Persona/Collaboratore from the anagrafica (robust) or a free-text name.
function CreateActivity({ current, horses, types, people, me, canManage, canOps, isMobile, date, onClose, onSaved }) {
  const [mode, setMode] = useState("single");
  const [sel, setSel] = useState([]);
  const [d, setD] = useState(date);
  const [commonTime, setCommonTime] = useState("");
  const [commonLoc, setCommonLoc] = useState("");
  const [assignee, setAssignee] = useState({ personId: "", name: "" });
  const [blocks, setBlocks] = useState([{ resp: "preparazione", typeId: "", time: "", loc: "", note: "", endTime: "", expEnd: "" }]);
  const [startNow, setStartNow] = useState(false);
  const [startNowHM, setStartNowHM] = useState(nowHM());
  const [rResp, setRResp] = useState("preparazione");
  const [rTypeId, setRTypeId] = useState("");
  const [wd, setWd] = useState([]);
  const [start, setStart] = useState(date);
  const [end, setEnd] = useState("");
  const [rTime, setRTime] = useState("");
  const [rLoc, setRLoc] = useState("");
  const [rNote, setRNote] = useState("");
  // Pianifica per persona (mode === "person")
  const [pTypeId, setPTypeId] = useState("");
  const [startTime, setStartTime] = useState("16:00");
  const [stepMin, setStepMin] = useState(60);
  const [order, setOrder] = useState([]);
  const [prepOn, setPrepOn] = useState(false);
  const [prepTypeId, setPrepTypeId] = useState("");
  const [prepAssignee, setPrepAssignee] = useState({ personId: "", name: "" });
  const [prepOffset, setPrepOffset] = useState(30);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState(null);

  const typesFor = (resp) => types.filter((t) => t.responsibility === resp);
  // Auto-propose a PLANNED end from the type's default duration (transitional, name-based).
  const proposeEnd = (typeId, startHM) => {
    const t = types.find((x) => x.id === typeId);
    const dur = t ? defaultDurationMin(t.name) : null;
    return dur && startHM ? addMinutesHM(startHM, dur) : "";
  };
  const setBlock = (i, patch) => setBlocks((bs) => bs.map((b, j) => j === i ? { ...b, ...patch } : b));
  const addBlock = () => setBlocks((bs) => [...bs, { resp: "attivita", typeId: "", time: "", loc: "", note: "", endTime: "", expEnd: "" }]);
  const removeBlock = (i) => setBlocks((bs) => bs.length > 1 ? bs.filter((_, j) => j !== i) : bs);
  const addMin = (t, m) => { const [h, mm] = (t || "0:0").split(":").map(Number); const v = Math.max(0, h * 60 + mm + m); return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`; };
  const seq = [...sel].sort((a, b) => { const ia = order.indexOf(a), ib = order.indexOf(b); return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib); });
  const seqTime = (idx) => (startTime ? addMin(startTime, stepMin * idx) : "");
  const moveSeq = (idx, dir) => { const j = idx + dir; if (j < 0 || j >= seq.length) return; const arr = [...seq]; [arr[idx], arr[j]] = [arr[j], arr[idx]]; setOrder(arr); };
  const hName = (id) => (horses.find((h) => h.id === id) || {}).display_name || id;

  // Gathers ALL soft warnings before creation: physical presence (fuori scuderia), operational
  // status (temp. indisponibile) — independent, both possible — plus Smart Conflict (attivita only).
  const gatherWarnings = async (horseIds, wDate, conflictChecks, person, personTimes) => {
    const res = { conflicts: {}, outside: [], unavailable: [], person: [] };
    const { data } = await api.post(`/stables/${current.id}/activity-plan/horse-warnings`, { horse_ids: horseIds, date: wDate });
    for (const [hid, info] of Object.entries(data.warnings || {})) {
      if (info.outside) res.outside.push({ horse_id: hid, name: info.display_name, ...info.outside });
      if (info.unavailable) res.unavailable.push({ horse_id: hid, name: info.display_name, ...info.unavailable });
    }
    if (conflictChecks && conflictChecks.length) {
      for (const c of conflictChecks) {
        if (!c.horse_ids.length) continue;
        const { data: cd } = await api.post(`/stables/${current.id}/activity-plan/check-conflict`, { horse_ids: c.horse_ids, date: wDate, responsibility: "attivita", time: c.time || null });
        Object.assign(res.conflicts, cd.conflicts || {});
      }
    }
    // Smart Conflict PERSONA (superable) — the assignee across Piano + Calendar (instructor/rider).
    if (person && (person.personId || (person.name || "").trim())) {
      const times = (personTimes && personTimes.length ? personTimes : [null]);
      const seen = new Set(); const seenEng = new Set();
      for (const t of times) {
        const k = t || "∅"; if (seen.has(k)) continue; seen.add(k);
        const { data: pd } = await api.post(`/stables/${current.id}/activity-plan/check-person-conflict`, {
          date: wDate, time: t || null, person_id: person.personId || null,
          name: person.personId ? null : (person.name || null),
        });
        for (const e of (pd.engagements || [])) {
          const ek = `${e.kind}|${e.label || e.lesson}|${e.time}`;
          if (!seenEng.has(ek)) { seenEng.add(ek); res.person.push(e); }
        }
      }
    }
    const any = res.outside.length || res.unavailable.length || Object.keys(res.conflicts).length || res.person.length;
    return any ? res : null;
  };
  const markReturnedAndSave = async () => {
    try { for (const o of (warn?.outside || [])) await api.post(`/stables/${current.id}/horses/${o.horse_id}/presence`, { presence_status: "presente" }); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
    setWarn(null); save(true);
  };

  const save = async (force = false) => {
    if (mode !== "routine" && !sel.length) { toast.error("Seleziona almeno un cavallo"); return; }
    setSaving(true);
    try {
      if (mode === "routine") {
        if (!rTypeId) { toast.error("Seleziona il tipo attività"); setSaving(false); return; }
        if (!wd.length) { toast.error("Seleziona i giorni"); setSaving(false); return; }
        if (!force) { const w = await gatherWarnings(sel, start, null, assignee, [rTime || null]); if (w) { setWarn(w); setSaving(false); return; } }
        await api.post(`/stables/${current.id}/activity-routines`, {
          horse_ids: sel, responsibility: rResp, activity_type_id: rTypeId, weekdays: wd,
          start_date: start, end_date: end || null, time: rTime || null,
          assignee_person_id: assignee.personId || null, assignee_name: assignee.personId ? null : (assignee.name || null),
          location_name: rLoc || null, note: rNote || null,
        });
      } else if (mode === "person") {
        if (!pTypeId) { toast.error("Seleziona il tipo attività"); setSaving(false); return; }
        const items = seq.map((hid, idx) => ({ horse_id: hid, time: seqTime(idx) || null }));
        if (!force) { const w = await gatherWarnings(seq, d, items.map((it) => ({ horse_ids: [it.horse_id], time: it.time })), assignee, items.map((it) => it.time)); if (w) { setWarn(w); setSaving(false); return; } }
        await api.post(`/stables/${current.id}/activities/plan-by-person`, {
          date: d, responsibility: "attivita", activity_type_id: pTypeId,
          assignee_person_id: assignee.personId || null, assignee_name: assignee.personId ? null : (assignee.name || null),
          location_name: commonLoc || null, items,
          prep: prepOn && prepTypeId ? { responsibility: "preparazione", activity_type_id: prepTypeId, offset_minutes: Number(prepOffset) || 30, assignee_person_id: prepAssignee.personId || null, assignee_name: prepAssignee.personId ? null : (prepAssignee.name || null) } : null,
        });
      } else {
        if (blocks.some((b) => !b.typeId)) { toast.error("Completa il tipo di ogni attività"); setSaving(false); return; }
        const effStart = startNow ? startNowHM : null;
        const asa = startNow ? `${d}T${startNowHM}:00` : null;
        if (!force) {
          const checks = blocks.filter((b) => b.resp === "attivita").map((b) => ({ horse_ids: sel, time: b.time || effStart || commonTime || null }));
          const w = await gatherWarnings(sel, d, checks, assignee, blocks.map((b) => b.time || effStart || commonTime || null)); if (w) { setWarn(w); setSaving(false); return; }
        }
        await api.post(`/stables/${current.id}/activities/batch`, {
          horse_ids: sel, date: d, time: (startNow ? startNowHM : commonTime) || null,
          assignee_person_id: assignee.personId || null, assignee_name: assignee.personId ? null : (assignee.name || null),
          location_name: commonLoc || null,
          blocks: blocks.map((b) => ({ responsibility: b.resp, activity_type_id: b.typeId, time: (b.time || effStart) || null, location_name: b.loc || null, note: b.note || null, expected_end_time: b.endTime || null, actual_start_at: asa })),
        });
      }
      toast.success("Creata"); onSaved();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <FormShell isMobile={isMobile} title="Nuova attività" testId="create-activity-form" onClose={onClose}>
      <div className="space-y-4">
        <div className="flex gap-1 bg-[#F0EAE1] rounded-lg p-1 flex-wrap">
          <button data-testid="mode-single" onClick={() => setMode("single")} className={`flex-1 py-1.5 rounded-md text-sm ${mode === "single" ? "bg-white shadow-sm" : "text-[#5F6B64]"}`}>Attività</button>
          <button data-testid="mode-person" onClick={() => setMode("person")} className={`flex-1 py-1.5 rounded-md text-sm ${mode === "person" ? "bg-white shadow-sm" : "text-[#5F6B64]"}`}>Pianifica per persona</button>
          {canManage && <button data-testid="mode-routine" onClick={() => setMode("routine")} className={`flex-1 py-1.5 rounded-md text-sm ${mode === "routine" ? "bg-white shadow-sm" : "text-[#5F6B64]"}`}>Routine</button>}
        </div>

        <div>
          <Label className="text-xs">Cavalli</Label>
          <div className="mt-1"><HorsePicker testId="create-horses" horses={horses} value={sel} onChange={setSel} multiple placeholder="Cerca cavallo (nome, microchip, UELN)…" /></div>
        </div>

        {mode === "single" && (
          <>
            <label className="flex items-center gap-2 rounded-lg border border-[#CDE3D4] bg-[#F1F7F2] px-3 py-2 text-sm text-[#1B3B2B] cursor-pointer" data-testid="start-now-wrap">
              <input type="checkbox" data-testid="start-now-toggle" checked={startNow} onChange={(e) => setStartNow(e.target.checked)} className="h-4 w-4 accent-[#1B5E20]" />
              <span className="font-medium">Inizia ora</span>
              <span className="text-xs text-[#5F6B64]">— parte subito (In corso)</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div><Label className="text-xs">Data</Label><Input type="date" className="mt-1 h-9 w-full" data-testid="single-date" value={d} onChange={(e) => setD(e.target.value)} /></div>
              {startNow
                ? <div><Label className="text-xs">Inizio effettivo</Label><Input type="time" className="mt-1 h-9 w-full" data-testid="start-now-time" value={startNowHM} onChange={(e) => setStartNowHM(e.target.value)} /></div>
                : <div><Label className="text-xs">Ora comune (opz.)</Label><Input type="time" className="mt-1 h-9 w-full" data-testid="common-time" value={commonTime} onChange={(e) => setCommonTime(e.target.value)} /></div>}
              <div><Label className="text-xs">Luogo comune (opz.)</Label><Input className="mt-1 h-9 w-full" data-testid="common-loc" value={commonLoc} onChange={(e) => setCommonLoc(e.target.value)} placeholder="Es. Campo coperto" /></div>
            </div>
            <div><Label className="text-xs">Assegnato a</Label><div className="mt-1"><PersonPicker testId="create-assignee" people={people} me={me} value={assignee} onChange={setAssignee} /></div></div>
            <div className="space-y-2">
              <Label className="text-xs">Attività</Label>
              {blocks.map((b, i) => (
                <div key={i} data-testid={`block-${i}`} className="rounded-lg border border-[#E5DEC9] p-2.5 space-y-2 bg-[#FBF9F4]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-[#5F6B64]">Area</Label>
                      <Select value={b.resp} onValueChange={(v) => setBlock(i, { resp: v, typeId: "" })}>
                        <SelectTrigger className="mt-1 h-9" data-testid={`block-resp-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{RESP.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] text-[#5F6B64]">Tipo attività</Label>
                      <Select value={b.typeId} onValueChange={(v) => setBlock(i, { typeId: v, endTime: b.endTime || proposeEnd(v, startNow ? startNowHM : (b.time || commonTime)) })}>
                        <SelectTrigger className="mt-1 h-9" data-testid={`block-type-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{typesFor(b.resp).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {!startNow && <div><Label className="text-[11px] text-[#5F6B64]">Ora inizio (override)</Label><Input type="time" className="mt-1 h-9 w-full" data-testid={`block-time-${i}`} value={b.time} onChange={(e) => setBlock(i, { time: e.target.value, endTime: b.endTime || proposeEnd(b.typeId, e.target.value) })} /></div>}
                    <div><Label className="text-[11px] text-[#5F6B64]">Fine prevista (opz.)</Label><Input type="time" className="mt-1 h-9 w-full" data-testid={`block-end-${i}`} value={b.endTime || ""} onChange={(e) => setBlock(i, { endTime: e.target.value })} /></div>
                  </div>
                  <div><Label className="text-[11px] text-[#5F6B64]">Luogo (override)</Label><Input className="mt-1 h-9 w-full" data-testid={`block-loc-${i}`} value={b.loc} onChange={(e) => setBlock(i, { loc: e.target.value })} /></div>
                  {blocks.length > 1 && <button data-testid={`block-remove-${i}`} onClick={() => removeBlock(i)} className="text-xs text-red-600 hover:underline">Rimuovi</button>}
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="add-block" onClick={addBlock}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi attività</Button>
            </div>
          </>
        )}

        {mode === "person" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Data</Label><Input type="date" className="mt-1 h-9" data-testid="person-date" value={d} onChange={(e) => setD(e.target.value)} /></div>
              <div><Label className="text-xs">Tipo attività</Label>
                <Select value={pTypeId} onValueChange={setPTypeId}>
                  <SelectTrigger className="mt-1 h-9" data-testid="person-type"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{typesFor("attivita").map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Persona</Label><div className="mt-1"><PersonPicker testId="person-assignee" people={people} me={me} value={assignee} onChange={setAssignee} /></div></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Ora inizio</Label><Input type="time" className="mt-1 h-9" data-testid="person-start" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></div>
              <div><Label className="text-xs">Step (min)</Label><Input type="number" className="mt-1 h-9" data-testid="person-step" value={stepMin} onChange={(e) => setStepMin(Number(e.target.value) || 0)} /></div>
            </div>
            <div className="rounded-lg border border-[#E5DEC9] divide-y" data-testid="person-seq">
              {seq.length === 0 && <p className="px-3 py-2 text-sm text-[#9AA39C]">Seleziona i cavalli sopra.</p>}
              {seq.map((hid, idx) => (
                <div key={hid} data-testid={`seq-row-${hid}`} draggable className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className="w-12 font-mono-label text-[#8C532B]">{seqTime(idx) || "—"}</span>
                  <span className="flex-1 text-[#1B3B2B]">{hName(hid)}</span>
                  <button data-testid={`seq-up-${hid}`} onClick={() => moveSeq(idx, -1)} disabled={idx === 0} className="text-[#8C6D4F] disabled:opacity-30"><ChevronLeft className="h-4 w-4 rotate-90" /></button>
                  <button data-testid={`seq-down-${hid}`} onClick={() => moveSeq(idx, 1)} disabled={idx === seq.length - 1} className="text-[#8C6D4F] disabled:opacity-30"><ChevronRight className="h-4 w-4 rotate-90" /></button>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-[#E5DEC9] p-2.5 bg-[#FBF9F4] space-y-2">
              <label className="flex items-center gap-2 text-sm text-[#1B3B2B]"><input type="checkbox" data-testid="prep-toggle" checked={prepOn} onChange={(e) => setPrepOn(e.target.checked)} />Preparazione cavalli collegata</label>
              {prepOn && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px] text-[#5F6B64]">Tipo preparazione</Label>
                      <Select value={prepTypeId} onValueChange={setPrepTypeId}>
                        <SelectTrigger className="mt-1 h-9" data-testid="prep-type"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{typesFor("preparazione").map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-[11px] text-[#5F6B64]">Anticipo</Label>
                      <Select value={String(prepOffset)} onValueChange={(v) => setPrepOffset(Number(v))}>
                        <SelectTrigger className="mt-1 h-9" data-testid="prep-offset"><SelectValue /></SelectTrigger>
                        <SelectContent>{[15, 30, 45, 60].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="text-[11px] text-[#5F6B64]">Preparato da</Label><div className="mt-1"><PersonPicker testId="prep-assignee" people={people} me={me} value={prepAssignee} onChange={setPrepAssignee} /></div></div>
                  <div className="text-xs text-[#5F6B64]" data-testid="prep-preview">
                    {seq.map((hid, idx) => <div key={hid}>{addMin(seqTime(idx), -prepOffset)} {hName(hid)} — Preparazione · {prepAssignee.name || "?"}</div>)}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {mode === "routine" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Area</Label>
                <Select value={rResp} onValueChange={(v) => { setRResp(v); setRTypeId(""); }}>
                  <SelectTrigger className="mt-1 h-9" data-testid="routine-resp"><SelectValue /></SelectTrigger>
                  <SelectContent>{RESP.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tipo attività</Label>
                <Select value={rTypeId} onValueChange={setRTypeId}>
                  <SelectTrigger className="mt-1 h-9" data-testid="routine-type"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{typesFor(rResp).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Giorni</Label>
              <div className="mt-1 flex gap-1 flex-wrap">
                {WD.map((l, i) => (
                  <button key={i} data-testid={`wd-${i}`} onClick={() => setWd((s) => s.includes(i) ? s.filter((x) => x !== i) : [...s, i])}
                    className={`h-8 w-9 rounded-md text-xs border ${wd.includes(i) ? "bg-[#1B3B2B] text-white border-[#1B3B2B]" : "border-[#E5DEC9] text-[#5F6B64]"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div><Label className="text-xs">Dal</Label><Input type="date" className="mt-1 h-9 w-full" data-testid="routine-start" value={start} onChange={(e) => setStart(e.target.value)} /></div>
              <div><Label className="text-xs">Al (opz.)</Label><Input type="date" className="mt-1 h-9 w-full" data-testid="routine-end" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
              <div><Label className="text-xs">Ora (opz.)</Label><Input type="time" className="mt-1 h-9 w-full" value={rTime} onChange={(e) => setRTime(e.target.value)} /></div>
            </div>
            <AssigneeBlock people={people} me={me} value={assignee} onChange={setAssignee} testId="routine-assignee" />
            <div><Label className="text-xs">Luogo (opz.)</Label><Input className="mt-1 h-9" value={rLoc} onChange={(e) => setRLoc(e.target.value)} placeholder="Es. Campo coperto" /></div>
            <div><Label className="text-xs">Nota (opz.)</Label><Textarea className="mt-1" value={rNote} onChange={(e) => setRNote(e.target.value)} /></div>
          </>
        )}

        {warn && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" data-testid="horse-warn">
            {warn.outside.length > 0 && (
              <div data-testid="warn-outside">
                <p className="font-semibold text-red-700">Cavalli fuori scuderia in questa data</p>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {warn.outside.map((o) => <li key={o.horse_id}>{o.name}: fuori scuderia{o.reason ? ` · ${o.reason}` : ""}{o.start_date ? ` · dal ${o.start_date}` : ""}{o.expected_return ? ` · rientro ${o.expected_return}` : ""}</li>)}
                </ul>
              </div>
            )}
            {warn.unavailable.length > 0 && (
              <div data-testid="warn-unavailable">
                <p className="font-semibold">Cavalli temporaneamente indisponibili</p>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {warn.unavailable.map((u) => <li key={u.horse_id}>{u.name}: {u.status === "ritirato" ? "ritirato" : "temporaneamente indisponibile"}{u.reason ? ` · ${u.reason}` : ""}{u.start_date ? ` · dal ${u.start_date}` : ""}{u.expected_return ? ` · rientro ${u.expected_return}` : ""}{u.reevaluation_date ? ` · rivalutazione ${u.reevaluation_date}` : ""}</li>)}
                </ul>
              </div>
            )}
            {Object.keys(warn.conflicts).length > 0 && (
              <div data-testid="warn-conflict">
                <p className="font-semibold">Cavalli già impegnati</p>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {Object.entries(warn.conflicts).map(([hid, engs]) => <li key={hid}>{hName(hid)}: {engs.map((e) => `${e.label || "impegno"}${e.time ? " " + e.time : ""}`).join(", ")}</li>)}
                </ul>
              </div>
            )}
            {warn.person.length > 0 && (
              <div data-testid="warn-person">
                <p className="font-semibold">{assignee.name || "Persona"} risulta già impegnata</p>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {warn.person.map((e, i) => <li key={i}>{personEngText(e)}</li>)}
                </ul>
              </div>
            )}
            <div className="flex gap-2 flex-wrap pt-1">
              <Button size="sm" variant="outline" data-testid="warn-review" onClick={() => setWarn(null)}>Rivedi</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="warn-proceed" disabled={saving} onClick={() => { setWarn(null); save(true); }}>Continua comunque</Button>
              {canOps && warn.outside.length > 0 && (
                <Button size="sm" className="bg-[#1B3B2B] hover:bg-[#142E1F] text-white" data-testid="warn-mark-returned" disabled={saving} onClick={markReturnedAndSave}>Segna rientrato e continua</Button>
              )}
            </div>
          </div>
        )}

        <Button className="w-full bg-[#1B3B2B] hover:bg-[#142E1F]" disabled={saving} data-testid="create-save" onClick={() => save(false)}>Crea</Button>
      </div>
    </FormShell>
  );
}

function EditActivity({ current, horses, types, people, me, canOps, isMobile, it, linkedPreps = [], onClose, onSaved }) {
  const isRoutineOcc = it.source === "routine";
  const [horseId, setHorseId] = useState(it.horse_id || "");
  const [d, setD] = useState(it.date || "");
  const [resp, setResp] = useState(it.responsibility || "preparazione");
  const [typeId, setTypeId] = useState(it.activity_type_id || "");
  const [time, setTime] = useState(it.time || "");
  const [assignee, setAssignee] = useState({ personId: it.assignee_person_id || "", name: it.assignee_name || "" });
  const [loc, setLoc] = useState(it.location_name || "");
  const [note, setNote] = useState(it.note || "");
  const hm = (iso) => (iso ? String(iso).slice(11, 16) : "");
  const [endTime, setEndTime] = useState(hm(it.expected_end_at));
  const openEnded = !!it.open_ended;  // preserved internally (no visible second semantic)
  const [linkPrompt, setLinkPrompt] = useState(null);
  const [warn, setWarn] = useState(null);
  const typesFor = (r) => types.filter((t) => t.responsibility === r);
  const addMin = (t, m) => { const [h, mm] = (t || "0:0").split(":").map(Number); const v = Math.max(0, h * 60 + mm + m); return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`; };

  const doSave = async () => {
    const body = { time: time || null, assignee_person_id: assignee.personId || null, assignee_name: assignee.personId ? null : (assignee.name || null), note, location_name: loc || null };
    if (!isRoutineOcc) {
      body.horse_id = horseId; body.date = d; body.responsibility = resp; body.activity_type_id = typeId;
      body.open_ended = openEnded;  // preserved (backward-compat only)
      body.expected_end_time = endTime || null;
    }
    await api.put(`/stables/${current.id}/activities/${it.id}`, body);
  };
  const finishSave = async () => {
    const timeChanged = (time || "") !== (it.time || "");
    await doSave();
    if (!isRoutineOcc && timeChanged && time && it.time && linkedPreps.length) {
      const delta = (parseInt(time.split(":")[0]) * 60 + parseInt(time.split(":")[1])) - (parseInt(it.time.split(":")[0]) * 60 + parseInt(it.time.split(":")[1]));
      setLinkPrompt({ delta });
      return;
    }
    toast.success(isRoutineOcc ? "Occorrenza modificata solo per oggi" : "Modificata"); onSaved();
  };
  const save = async (force = false) => {
    try {
      if (!force) {
        let w = null;
        const wDate = isRoutineOcc ? it.date : d;
        if (!isRoutineOcc && (horseId !== it.horse_id || d !== it.date)) {
          const { data } = await api.post(`/stables/${current.id}/activity-plan/horse-warnings`, { horse_ids: [horseId], date: d });
          const info = data.warnings?.[horseId];
          if (info && (info.outside || info.unavailable)) w = { ...(w || {}), name: info.display_name, outside: info.outside, unavailable: info.unavailable };
        }
        if (assignee.personId || (assignee.name || "").trim()) {
          const { data: pd } = await api.post(`/stables/${current.id}/activity-plan/check-person-conflict`, {
            date: wDate, time: time || null, person_id: assignee.personId || null,
            name: assignee.personId ? null : (assignee.name || null), exclude_activity_id: it.id });
          if ((pd.engagements || []).length) w = { ...(w || {}), personName: assignee.name, person: pd.engagements };
        }
        if (w) { setWarn(w); return; }
      }
      await finishSave();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const markReturnedAndSave = async () => {
    try { await api.post(`/stables/${current.id}/horses/${horseId}/presence`, { presence_status: "presente" }); } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    setWarn(null); save(true);
  };
  const applyLinked = async (yes) => {
    try {
      if (yes) {
        for (const p of linkedPreps) {
          if (p.time) await api.put(`/stables/${current.id}/activities/${p.id}`, { time: addMin(p.time, linkPrompt.delta) });
        }
      }
      toast.success("Modificata"); setLinkPrompt(null); onSaved();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  return (
    <FormShell isMobile={isMobile} title={isRoutineOcc ? "Modifica solo questa volta" : "Modifica attività"} testId="edit-activity-form" onClose={onClose}>
      <div className="space-y-4">
        {!isRoutineOcc ? (
          <>
            <div>
              <Label className="text-xs">Cavallo</Label>
              <div className="mt-1"><HorsePicker testId="edit-horse" horses={horses} value={horseId} onChange={(id) => setHorseId(id)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Data</Label><Input type="date" className="mt-1 h-9" data-testid="edit-date" value={d} onChange={(e) => setD(e.target.value)} /></div>
              <div><Label className="text-xs">Ora inizio</Label><Input type="time" className="mt-1 h-9" data-testid="edit-time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Fine prevista (opz.)</Label><Input type="time" className="mt-1 h-9" data-testid="edit-end" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Area</Label>
                <Select value={resp} onValueChange={(v) => { setResp(v); setTypeId(""); }}>
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-resp"><SelectValue /></SelectTrigger>
                  <SelectContent>{RESP.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tipo attività</Label>
                <Select value={typeId} onValueChange={(v) => { setTypeId(v); const t = types.find((x) => x.id === v); const dur = t ? defaultDurationMin(t.name) : null; if (dur && time && !endTime) setEndTime(addMinutesHM(time, dur)); }}>
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-type"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{typesFor(resp).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </>
        ) : (
          <div><Label className="text-xs">Ora</Label><Input type="time" className="mt-1 h-9" data-testid="edit-time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
        )}
        <AssigneeBlock people={people} me={me} value={assignee} onChange={setAssignee} testId="edit-assignee" />
        <div><Label className="text-xs">Luogo</Label><Input className="mt-1 h-9" data-testid="edit-loc" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Es. Campo coperto" /></div>
        <div><Label className="text-xs">Nota</Label><Textarea className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} /></div>
        {warn && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" data-testid="edit-horse-warn">
            {warn.outside && <p className="font-semibold text-red-700" data-testid="edit-warn-outside">{warn.name}: risulta fuori scuderia in questa data{warn.outside.reason ? ` · ${warn.outside.reason}` : ""}{warn.outside.expected_return ? ` · rientro ${warn.outside.expected_return}` : ""}</p>}
            {warn.unavailable && <p className="font-semibold" data-testid="edit-warn-unavailable">{warn.name}: temporaneamente indisponibile{warn.unavailable.reason ? ` · ${warn.unavailable.reason}` : ""}</p>}
            {warn.person && warn.person.length > 0 && (
              <div data-testid="edit-warn-person">
                <p className="font-semibold">{warn.personName || "Persona"} risulta già impegnata</p>
                <ul className="mt-1 list-disc pl-5 text-xs">{warn.person.map((e, i) => <li key={i}>{personEngText(e)}</li>)}</ul>
              </div>
            )}
            <div className="flex gap-2 flex-wrap pt-1">
              <Button size="sm" variant="outline" onClick={() => setWarn(null)}>Rivedi</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="edit-warn-proceed" onClick={() => { setWarn(null); save(true); }}>Continua comunque</Button>
              {canOps && warn.outside && <Button size="sm" className="bg-[#1B3B2B] hover:bg-[#142E1F] text-white" data-testid="edit-warn-mark-returned" onClick={markReturnedAndSave}>Segna rientrato e continua</Button>}
            </div>
          </div>
        )}
        <Button className="w-full bg-[#1B3B2B] hover:bg-[#142E1F]" data-testid="edit-save" onClick={() => save(false)}>Salva</Button>
      </div>
      <Dialog open={!!linkPrompt} onOpenChange={(o) => { if (!o) { setLinkPrompt(null); onSaved(); } }}>
        <DialogContent data-testid="linked-prep-dialog">
          <DialogHeader><DialogTitle>Aggiornare anche la preparazione collegata?</DialogTitle>
            <DialogDescription>Hai spostato l'orario dell'attività. La preparazione collegata verrebbe spostata di conseguenza (nuovo orario: {linkPrompt && linkedPreps[0]?.time ? addMin(linkedPreps[0].time, linkPrompt.delta) : "—"}).</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="linked-keep" onClick={() => applyLinked(false)}>No, lascia invariata</Button>
            <Button className="bg-[#1B3B2B] hover:bg-[#142E1F]" data-testid="linked-update" onClick={() => applyLinked(true)}>Sì, aggiorna</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FormShell>
  );
}
